/**
 * ============================================================================
 * OBJECTS STORE - CACHE RETRIEVAL BY TYPE
 * ============================================================================
 * 
 * Functions for retrieving cached objects filtered by their type.
 * 
 * Object Types:
 * - "glossary" / "Library Object": Standard library items
 * - "document": Document-type items
 * 
 * These functions read from the local cache and don't trigger API calls.
 * Call fetchObjects() first to ensure the cache is populated.
 * 
 * @module objects/cache/byType
 */

import { objectsStore } from "../store";
import type { ObjectItem } from "../types";

// ============================================================================
// TYPE CONSTANTS
// ============================================================================

/** Types considered as "library" objects */
const LIBRARY_TYPES = ['glossary', 'Library Object'] as const;

/** Document type identifier */
const DOCUMENT_TYPE = 'document';

// ============================================================================
// CACHE RETRIEVAL BY TYPE
// ============================================================================

/**
 * Get cached objects of library type (glossary/Library Object) for a specific game.
 * 
 * Note: This reads from local cache. Call fetchObjects() first to populate the cache.
 * 
 * @param gameId - The game ID to filter by
 * @returns Array of cached library objects
 * 
 * @example
 * ```ts
 * const libraryObjects = getCachedObjects(gameId);
 * ```
 */
export function getCachedObjects(gameId: string): ObjectItem[] {
  const allItems = objectsStore.getData();
  return allItems.filter(item => 
    item.game_id === gameId && 
    (item.type === 'glossary' || item.type === 'Library Object')
  );
}

/**
 * Get cached document items for a specific game.
 * 
 * @param gameId - The game ID to filter by
 * @returns Array of cached document items
 * 
 * @example
 * ```ts
 * const documents = getCachedDocumentItems(gameId);
 * ```
 */
export function getCachedDocumentItems(gameId: string): ObjectItem[] {
  const allItems = objectsStore.getData();
  return allItems.filter(item => 
    item.game_id === gameId && 
    item.type === DOCUMENT_TYPE
  );
}

/**
 * Get cached library items (includes both glossary and document types).
 * 
 * Use this when you need all items that can appear in the library,
 * regardless of their specific type.
 * 
 * @param gameId - The game ID to filter by
 * @returns Array of cached library items (glossary + documents)
 * 
 * @example
 * ```ts
 * const allLibraryItems = getCachedLibraryItems(gameId);
 * ```
 */
export function getCachedLibraryItems(gameId: string): ObjectItem[] {
  const allItems = objectsStore.getData();
  return allItems.filter(item => 
    item.game_id === gameId && 
    (item.type === 'glossary' || item.type === 'Library Object' || item.type === DOCUMENT_TYPE)
  );
}
